// PDF.js 类型声明
declare const PDFJS: {
    workerSrc: string;
    getDocument: (url: string) => Promise<{
        numPages: number;
        getPage: (pageNumber: number) => Promise<{
            getViewport: (scale: number) => { width: number; height: number };
            render: (renderContext: {
                canvasContext: CanvasRenderingContext2D;
                viewport: { width: number; height: number };
            }) => Promise<void>;
        }>;
    }>;
};

// PDF 页面类型
type PDFPage = {
    getViewport: (scale: number) => { width: number; height: number };
    render: (renderContext: {
        canvasContext: CanvasRenderingContext2D;
        viewport: { width: number; height: number };
    }) => Promise<void>;
};

// PDF 文档类型
type PDFDocument = {
    numPages: number;
    getPage: (pageNumber: number) => Promise<PDFPage>;
};

// PDFViewer 配置选项
interface PDFViewerOptions {
    /** PDF 文件 URL */
    url: string;
    /** Canvas 元素或选择器 */
    canvas: HTMLCanvasElement | string;
    /** Worker 文件路径，默认使用相对路径 */
    workerSrc: string;
    /** 缩放比例，默认 1.5 */
    scale?: number;
    /** 初始页码，默认 1 */
    initialPage?: number;
    /** 页面渲染完成回调 */
    onPageRendered?: (pageNumber: number, totalPages: number) => void;
    /** PDF 加载完成回调 */
    onLoaded?: (totalPages: number) => void;
    /** 错误回调 */
    onError?: (error: Error) => void;
}

// PDFViewer 控制接口
interface PDFViewerControls {
    /** 跳转到上一页 */
    prev: () => void;
    /** 跳转到下一页 */
    next: () => void;
    /** 跳转到指定页码 */
    goToPage: (pageNumber: number) => void;
    /** 获取当前页码 */
    getCurrentPage: () => number;
    /** 获取总页数 */
    getTotalPages: () => number;
    /** 重新渲染当前页 */
    render: () => void;
    /** 销毁实例 */
    destroy: () => void;
}

/**
 * 创建 PDF 查看器实例
 * @param options 配置选项
 * @returns PDFViewer 控制对象
 */
export function createPDFViewer(options: PDFViewerOptions): PDFViewerControls {
    // 检查 PDFJS 是否加载
    if (typeof PDFJS === "undefined") {
        throw new Error("PDFJS 未加载，请检查 pdf.min.js 是否正确引入");
    }

    // 解析配置
    const {
        url,
        canvas: canvasInput,
        scale = 1.5,
        workerSrc,
        initialPage = 1,
        onPageRendered,
        onLoaded,
        onError,
    } = options;

    if (!workerSrc) {
        throw new Error("workerSrc 不能为空");
    }

    // 设置 worker
    PDFJS.workerSrc = workerSrc;

    // 解析 canvas
    const canvas =
        typeof canvasInput === "string"
            ? (document.querySelector(canvasInput) as HTMLCanvasElement)
            : canvasInput;

    if (!canvas) {
        throw new Error("找不到 canvas 元素");
    }

    // 状态管理
    let pdfDoc: PDFDocument | null = null;
    let currentPage = initialPage;
    let isRendering = false;

    // 渲染指定页面
    function renderPage(pageNumber: number): Promise<void> {
        // @ts-ignore - Promise 在 es5 中需要类型声明，但运行时支持
        return new Promise<void>((resolve: () => void, reject: (error: Error) => void) => {
            if (!pdfDoc) {
                const error = new Error("PDF 文档未加载");
                reject(error);
                return;
            }

            if (pageNumber < 1 || pageNumber > pdfDoc.numPages) {
                const error = new Error(
                    `页码 ${pageNumber} 超出范围 (1-${pdfDoc.numPages})`
                );
                reject(error);
                return;
            }

            if (isRendering) {
                console.warn("正在渲染中，请稍候...");
                return;
            }

            isRendering = true;
            currentPage = pageNumber;

            pdfDoc!
                .getPage(pageNumber)
                .then((page: PDFPage) => {
                    const viewport = page.getViewport(scale);

                    const context = canvas.getContext("2d");
                    if (!context) {
                        throw new Error("无法获取 canvas 2d 上下文");
                    }

                    canvas.width = viewport.width;
                    canvas.height = viewport.height;

                    const renderContext = {
                        canvasContext: context,
                        viewport: viewport,
                    };

                    return page.render(renderContext);
                })
                .then(() => {
                    isRendering = false;
                    if (onPageRendered && pdfDoc) {
                        onPageRendered(currentPage, pdfDoc.numPages);
                    }
                    resolve();
                })
                .catch((error: Error) => {
                    isRendering = false;
                    console.error("页面渲染失败:", error);
                    if (onError) {
                        onError(error);
                    }
                    reject(error);
                });
        });
    }

    // 控制方法
    const controls: PDFViewerControls = {
        prev: () => {
            if (!pdfDoc || currentPage <= 1) return;
            renderPage(currentPage - 1).catch((error) => {
                if (onError) onError(error);
            });
        },

        next: () => {
            if (!pdfDoc || !pdfDoc.numPages) return;
            if (currentPage >= pdfDoc.numPages) return;
            renderPage(currentPage + 1).catch((error) => {
                if (onError) onError(error);
            });
        },

        goToPage: (pageNumber: number) => {
            renderPage(pageNumber).catch((error) => {
                if (onError) onError(error);
            });
        },

        getCurrentPage: () => currentPage,

        getTotalPages: () => (pdfDoc ? pdfDoc.numPages : 0),

        render: () => {
            renderPage(currentPage).catch((error) => {
                if (onError) onError(error);
            });
        },

        destroy: () => {
            pdfDoc = null;
        },
    };

    // 加载 PDF 文档
    PDFJS.getDocument(url)
        .then((pdf: PDFDocument) => {
            console.log("PDF 加载成功，总页数:", pdf.numPages);
            pdfDoc = pdf;

            // 确保初始页码在有效范围内
            const validInitialPage = Math.max(1, Math.min(initialPage, pdf.numPages));
            currentPage = validInitialPage;

            if (onLoaded) {
                onLoaded(pdf.numPages);
            }

            // 渲染初始页
            return renderPage(currentPage);
        })
        .catch((error: Error) => {
            console.error("PDF 加载失败:", error);
            if (onError) {
                onError(error);
            }
        });

    return controls;
}